import { FC } from 'react';
import dayjs from 'dayjs';
import { IActivity as Activity } from '@/utils/activity-calendar/interfaces/IActivity';
import { IAnchor as Anchor } from '@/utils/activity-calendar/interfaces/IAnchor';
import { ActivityStatus } from '@/utils/activity-calendar/enums/ActivityCalendar.enum';
import {
  DAY_NAMES,
  DAYS_PER_WEEK,
  FIRST_DAY_OF_MONTH,
  MONDAY_FIRST_WEEK_OFFSET,
  getTypeMeta,
} from '@/utils/activity-calendar/constants';
import { dayKey, getRecordPath, sortByType } from '@/utils/activity-calendar/services/activityCalendarUtils';

const MAX_VISIBLE_MONTH_ACTIVITIES = 3;

interface IProps {
  idx: Record<string, Activity[]>;
  anchor: Anchor;
  today: dayjs.Dayjs;
  onActivityClick: (activity: Activity) => void;
}

const ActivityChip: FC<{ activity: Activity; onClick: (() => void) | undefined }> = ({ activity, onClick }) => {
  const t = getTypeMeta(activity.type);
  const planned = activity.status === ActivityStatus.Planned;

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      title={`${t.label}: ${activity.title} — ${activity.farm}, ${activity.block} (${activity.status})`}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      className="flex items-center gap-[5px] overflow-hidden text-ellipsis whitespace-nowrap rounded-[6px] px-[6px] py-[2px] text-[11px] font-semibold leading-[1.35]"
      style={{
        background: planned ? 'transparent' : t.bg,
        color: t.fg,
        border: planned ? `1px dashed ${t.color}` : '1px solid transparent',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span className="h-[7px] w-[7px] flex-[0_0_auto] rounded-full" style={{ background: t.color }} />
      <span className="overflow-hidden text-ellipsis">{activity.title}</span>
    </div>
  );
};

const MonthCell: FC<{
  date: dayjs.Dayjs;
  currentMonthIndex: number;
  idx: IProps['idx'];
  today: dayjs.Dayjs;
  onActivityClick: IProps['onActivityClick'];
}> = ({ date, currentMonthIndex, idx, today, onActivityClick }) => {
  const acts = sortByType(idx[dayKey(date)] || []);
  const inMonth = date.month() === currentMonthIndex;
  const isToday = date.isSame(today, 'day');

  return (
    <div
      className="flex min-h-[94px] flex-col gap-[4px] overflow-hidden rounded-[10px] border p-[7px]"
      style={{
        background: inMonth ? '#fff' : '#F7F8F5',
        borderColor: isToday ? '#2D855F' : '#E5E7EB',
        borderWidth: isToday ? 1.5 : 1,
        boxShadow: isToday ? '0 0 0 3px rgb(45 133 95 / 0.2)' : undefined,
      }}
    >
      <div className="flex items-center justify-between">
        <span
          className="text-[13px]"
          style={{
            fontWeight: isToday ? 800 : 600,
            color: inMonth ? (isToday ? '#2D855F' : '#1F2937') : '#9CA3AF',
          }}
        >
          {date.date()}
        </span>
        {isToday && (
          <span className="rounded-full bg-primary px-[6px] py-[2px] text-[9px] font-extrabold tracking-[0.06em] text-white">
            TODAY
          </span>
        )}
      </div>
      {acts.slice(0, MAX_VISIBLE_MONTH_ACTIVITIES).map((a) => (
        <ActivityChip
          key={a.id}
          activity={a}
          onClick={getRecordPath(a) ? () => onActivityClick(a) : undefined}
        />
      ))}
      {acts.length > MAX_VISIBLE_MONTH_ACTIVITIES && (
        <div className="pl-[2px] text-[11px] font-semibold text-[#6B7280]">
          +{acts.length - MAX_VISIBLE_MONTH_ACTIVITIES} more
        </div>
      )}
    </div>
  );
};

export const MonthView: FC<IProps> = ({ idx, anchor, today, onActivityClick }) => {
  const first = dayjs(new Date(anchor.year, anchor.monthIndex, FIRST_DAY_OF_MONTH));
  const startOff = (first.day() + MONDAY_FIRST_WEEK_OFFSET) % DAYS_PER_WEEK;
  const dim = first.daysInMonth();
  const weeksNeeded = Math.ceil((startOff + dim) / DAYS_PER_WEEK);
  const gridStart = first.subtract(startOff, 'day');

  const rows: JSX.Element[] = [];
  let cursor = gridStart;
  for (let weekIndex = 0; weekIndex < weeksNeeded; weekIndex++) {
    const weekStartKey = dayKey(cursor);
    const week: JSX.Element[] = [];
    for (let dayIndex = 0; dayIndex < DAYS_PER_WEEK; dayIndex++) {
      week.push(
        <MonthCell
          key={dayKey(cursor)}
          date={cursor}
          currentMonthIndex={anchor.monthIndex}
          idx={idx}
          today={today}
          onActivityClick={onActivityClick}
        />,
      );
      cursor = cursor.add(1, 'day');
    }
    rows.push(
      <div key={weekStartKey} className="grid grid-cols-7 gap-[8px]">
        {week}
      </div>,
    );
  }

  return (
    <div>
      <div className="mb-[8px] grid grid-cols-7 gap-[8px]">
        {DAY_NAMES.map((d) => (
          <div
            key={d}
            className="text-center text-[12px] font-bold uppercase tracking-[0.06em] text-[#6B7280]"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-[8px]">{rows}</div>
    </div>
  );
};
